import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { getThemeColors, THEME_OPTIONS } from '../theme'

const ADMIN_EMAILS = ['acuneo@gmail.com']
const PROD_FUNCTIONS_BASE_URL = 'https://fvnuxqgzkeghaimgqqcz.supabase.co/functions/v1'

const getFunctionsBaseUrl = () => {
  // Siempre usar produccion
  return 'https://fvnuxqgzkeghaimgqqcz.supabase.co/functions/v1'
}

const FUNCTIONS_BASE_URL = getFunctionsBaseUrl()
const SET_BETA_ACCESS_URL = `${FUNCTIONS_BASE_URL}/set-beta-access`
const ADMIN_DELETE_USER_URL = `${FUNCTIONS_BASE_URL}/admin-delete-user`
const ADMIN_LIST_USERS_URL = `${FUNCTIONS_BASE_URL}/admin-list-users`
const ADMIN_SET_USER_FEATURES_URL = `${FUNCTIONS_BASE_URL}/admin-set-user-features`

const formatDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
}

const getUserType = (user) => {
  if (ADMIN_EMAILS.includes(user.email || '')) return { label: 'admin', bg: '#EFF6FF', color: '#2563EB' }
  if (user.beta_access) return { label: 'beta', bg: '#F0FDF4', color: '#15803d' }
  return { label: 'waitlist', bg: '#fffbeb', color: '#b45309' }
}

export default function Admin({ session, theme = THEME_OPTIONS.LIGHT }) {
  const C = getThemeColors(theme)
  const navigate = useNavigate()
  const FALLBACK_MODE = true
  const [loading, setLoading] = useState(true)
  const [savingState, setSavingState] = useState({ userId: null, action: null })
  const [bulkApproving, setBulkApproving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [users, setUsers] = useState([])
  const [featureDrafts, setFeatureDrafts] = useState({})

  const isAdmin = ADMIN_EMAILS.includes(session?.user?.email || '')

  const toggleFeatureDraft = (userId, key, checked) => {
    setFeatureDrafts((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [key]: checked,
      },
    }))
  }

  const saveUserFeatures = async (userId) => {
    if (!isAdmin) {
      setError('Solo admin puede hacer esto')
      return
    }

    const draft = featureDrafts[userId]
    if (!draft) {
      setError('No hay cambios que guardar')
      return
    }

    setSavingState({ userId, action: 'features' })
    setError('')
    setSuccess('')
    try {
      console.log('[Admin] Guardando features:', { userId, draft })

      const { data: { session: currentSession } } = await supabase.auth.getSession()
      if (!currentSession?.access_token) {
        throw new Error('No se encontro una sesion valida para guardar features.')
      }




      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 12000)

      const response = await fetch(ADMIN_SET_USER_FEATURES_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          access_token: currentSession.access_token,
          user_id: userId,
          premium_enabled: draft.premium_enabled === true,
          unlimited_keywords: draft.unlimited_keywords === true,
          manual_daily_search: draft.manual_daily_search === true,
        }),
      })

      clearTimeout(timeoutId)





      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.error || 'No pudimos guardar features.')
      }

      setSuccess('Features del usuario actualizadas.')
      await loadData()
    } catch (err) {
      console.error('[Admin] saveUserFeatures', err)
      if (err?.name === 'AbortError') {
        setError('Tiempo de espera agotado al guardar features. Intenta de nuevo.')
      } else {
        setError(`No pudimos actualizar features.${err?.message ? ` ${err.message}` : ''}`)
      }
    } finally {
      setSavingState({ userId: null, action: null })
    }
  }

  useEffect(() => {
    if (!isAdmin) return
    loadData()
  }, [isAdmin])

  const loadDataFallback = async () => {
    console.warn('[Admin] Fallback: cargando usuarios por lectura directa')

    const { data: waitlistUsers, error: waitlistError } = await supabase
      .from('waitlist')
      .select('*')
      .order('created_at', { ascending: false })

    if (waitlistError) throw waitlistError

    const { data: adminUser } = await supabase
      .from('profiles')
      .select('*')
      .eq('email', 'acuneo@gmail.com')
      .single()

    const waitlist = waitlistUsers || []
    const emails = [...new Set(waitlist.map((w) => w.email).filter(Boolean))]
    let profileByEmail = {}
    if (emails.length > 0) {
      const { data: profilesBatch } = await supabase
        .from('profiles')
        .select('*')
        .in('email', emails)

      profileByEmail = (profilesBatch || []).reduce((acc, row) => {
        acc[row.email] = row
        return acc
      }, {})
    }

    const userIds = [...new Set(waitlist.map((w) => w.user_id).filter(Boolean))]
    let searchByUser = {}
    let featureFlagsByUser = {}
    let jobsByUser = {}
    if (userIds.length > 0) {
      const { data: searchBatch } = await supabase
        .from('search_profiles')
        .select('user_id, keywords, updated_at')
        .in('user_id', userIds)

      searchByUser = (searchBatch || []).reduce((acc, row) => {
        acc[row.user_id] = row
        return acc
      }, {})

      const { data: flagsBatch } = await supabase
        .from('feature_flags')
        .select('user_id, feature, enabled')
        .in('feature', ['beta_access', 'premium_enabled', 'unlimited_keywords', 'manual_daily_search'])
        .in('user_id', userIds)

      featureFlagsByUser = (flagsBatch || []).reduce((acc, row) => {
        if (!acc[row.user_id]) acc[row.user_id] = {}
        acc[row.user_id][row.feature] = row.enabled === true
        return acc
      }, {})

      const { data: jobsBatch } = await supabase
        .from('jobs')
        .select('user_id, created_at')
        .in('user_id', userIds)

      jobsByUser = (jobsBatch || []).reduce((acc, row) => {
        if (!acc[row.user_id]) {
          acc[row.user_id] = { jobs_count: 0, last_job_at: null }
        }
        acc[row.user_id].jobs_count += 1
        if (!acc[row.user_id].last_job_at || new Date(row.created_at) > new Date(acc[row.user_id].last_job_at)) {
          acc[row.user_id].last_job_at = row.created_at
        }
        return acc
      }, {})
    }

    const enrichedWaitlist = waitlist.map((wUser) => {
      const pUser = profileByEmail[wUser.email]
      const sUser = searchByUser[wUser.user_id]
      const userFlags = featureFlagsByUser[wUser.user_id] || {}
      const userJobs = jobsByUser[wUser.user_id] || { jobs_count: 0, last_job_at: null }
      const keywordsList = Array.isArray(sUser?.keywords) ? sUser.keywords.filter(Boolean) : []
      const betaAccessFromFlags = userFlags.beta_access === true
      const betaAccessFromProfile = pUser?.beta_access === true
      const betaAccessEffective = betaAccessFromFlags || betaAccessFromProfile
      return {
        id: pUser?.id || wUser.user_id,
        email: wUser.email,
        nombre: pUser?.nombre,
        user_id: wUser.user_id,
        beta_access: betaAccessEffective,
        beta_access_profile: betaAccessFromProfile,
        beta_access_mismatch: betaAccessFromFlags !== betaAccessFromProfile,
        premium_enabled: userFlags.premium_enabled === true,
        unlimited_keywords: userFlags.unlimited_keywords === true,
        manual_daily_search: userFlags.manual_daily_search === true,
        joined_at: wUser.created_at,
        onboarding_updated_at: sUser?.updated_at || pUser?.created_at,
        keywords_count: keywordsList.length,
        keywords_list: keywordsList,
        jobs_count: userJobs.jobs_count,
        last_job_at: userJobs.last_job_at,
      }
    })

    const enrichedAdmin = adminUser ? {
      id: adminUser.id,
      email: adminUser.email,
      nombre: adminUser.nombre,
      user_id: adminUser.id,
      beta_access: true,
      beta_access_profile: adminUser.beta_access === true,
      beta_access_mismatch: false,
      premium_enabled: false,
      unlimited_keywords: false,
      manual_daily_search: false,
      joined_at: adminUser.created_at,
      onboarding_updated_at: adminUser.created_at,
      keywords_count: 0,
      keywords_list: [],
      jobs_count: 0,
      last_job_at: null,
    } : null

    const allUsers = enrichedAdmin ? [enrichedAdmin, ...enrichedWaitlist] : enrichedWaitlist
    const normalizedUsers = allUsers

    setUsers(Array.isArray(normalizedUsers) ? normalizedUsers : [])
    const drafts = {}
    normalizedUsers?.forEach(user => {
      const featureKey = user.user_id || user.id
      drafts[featureKey] = {
        premium_enabled: user.premium_enabled === true,
        unlimited_keywords: user.unlimited_keywords === true,
        manual_daily_search: user.manual_daily_search === true,
      }
    })
    setFeatureDrafts(drafts)
  }

  const loadData = async () => {
    setLoading(true)
    setError('')
    try {
      await loadDataFallback()
      setError('')
    } catch (err) {
      console.error('[Admin] loadData error:', err)
      setError(`No pudimos cargar el panel admin. ${err?.message || 'Error desconocido'}`)
    } finally {
      setLoading(false)
    }
  }

  const pendingCount = useMemo(() => users.filter((u) => !u.beta_access).length, [users])
  const activeCount = useMemo(() => users.filter((u) => u.beta_access).length, [users])

  const setBetaAccess = async (userId, enabled) => {
    if (!isAdmin) {
      setError('Solo admin puede hacer esto')
      return
    }
    
    setSavingState({ userId, action: enabled ? 'approve' : 'revoke' })
    setError('')
    setSuccess('')
    try {
      // Obtener el email del usuario
      const user = users.find(u => u.id === userId || u.user_id === userId)
      if (!user) throw new Error('Usuario no encontrado')

      // 1) Fuente real de acceso beta: Edge Function con service role
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      if (!currentSession?.access_token) {
        throw new Error('No se encontro una sesion valida para actualizar acceso beta.')
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 12000)
      const response = await fetch(SET_BETA_ACCESS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          access_token: currentSession.access_token,
          user_id: user.id || user.user_id,
          enabled,
        }),
      })
      clearTimeout(timeoutId)

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.error || 'No pudimos actualizar acceso beta.')
      }

      // Reflejar el cambio en UI inmediatamente para evitar estado visual desfasado.
      setUsers((prev) => prev.map((item) => (
        (item.id === userId || item.user_id === userId)
          ? { ...item, beta_access: enabled }
          : item
      )))

      // 2) Mantener compatibilidad en profiles para vistas antiguas
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id || user.user_id,
          email: user.email,
          beta_access: enabled,
        }, { onConflict: 'id' })

      if (profileError) throw profileError

      setSuccess(enabled ? 'Usuario aprobado correctamente.' : 'Acceso beta revocado correctamente.')
      await loadData()
    } catch (err) {
      console.error('[Admin] setBetaAccess', err)
      setError(`No pudimos actualizar el acceso beta.${err?.message ? ` ${err.message}` : ''}`)
    } finally {
      setSavingState({ userId: null, action: null })
    }
  }

  const approveAllPending = async () => {
    if (!isAdmin || bulkApproving) return

    const pendingUsers = users.filter((u) => !u.beta_access)
    if (pendingUsers.length === 0) {
      setSuccess('No hay usuarios pendientes por aprobar.')
      return
    }

    const confirmed = window.confirm(`Se aprobaran ${pendingUsers.length} usuarios pendientes. Continuar?`)
    if (!confirmed) return

    const typed = window.prompt(`Escribe ${pendingUsers.length} para confirmar la aprobacion masiva`)
    if (typed !== String(pendingUsers.length)) {
      setError('Aprobacion masiva cancelada. Confirmacion invalida.')
      return
    }

    setBulkApproving(true)
    setError('')
    setSuccess('')
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      if (!currentSession?.access_token) {
        throw new Error('No se encontro una sesion valida para aprobar en lote.')
      }

      const approvals = pendingUsers.map(async (user) => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 12000)
        try {
          const response = await fetch(SET_BETA_ACCESS_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
              access_token: currentSession.access_token,
              user_id: user.id || user.user_id,
              enabled: true,
            }),
          })
          const payload = await response.json().catch(() => ({}))
          if (!response.ok) {
            throw new Error(payload?.error || `No pudimos aprobar a ${user.email}.`)
          }
          return { ok: true, id: user.id || user.user_id, email: user.email }
        } finally {
          clearTimeout(timeoutId)
        }
      })

      const settled = await Promise.allSettled(approvals)
      const approved = settled
        .filter((r) => r.status === 'fulfilled' && r.value?.ok)
        .map((r) => r.value)
      const failed = settled.length - approved.length

      const approvedIds = approved.map((r) => r.id)
      if (approvedIds.length > 0) {
        const approvedRows = approved.map((r) => ({
          id: r.id,
          email: r.email,
          beta_access: true,
        }))
        const { error: profileUpsertError } = await supabase
          .from('profiles')
          .upsert(approvedRows, { onConflict: 'id' })
        if (profileUpsertError) {
          throw profileUpsertError
        }
      }

      setUsers((prev) => prev.map((item) => (
        approvedIds.includes(item.id || item.user_id)
          ? { ...item, beta_access: true }
          : item
      )))
      await loadData()
      if (failed > 0) {
        setError(`Aprobacion parcial: ${approvedIds.length} ok, ${failed} con error.`)
      } else {
        setSuccess(`Se aprobaron ${approvedIds.length} usuarios.`)
      }
    } catch (err) {
      console.error('[Admin] approveAllPending', err)
      setError(`No pudimos completar la aprobacion en lote.${err?.message ? ` ${err.message}` : ''}`)
    } finally {
      setBulkApproving(false)
    }
  }

  const deleteUser = async (userId, email) => {
    if (!isAdmin) {
      setError('Solo admin puede hacer esto')
      return
    }

    const confirmed = window.confirm(`¿Eliminar por completo a ${email}?\n\nEsto borrará su acceso y datos de prueba.`)
    if (!confirmed) return

    setSavingState({ userId, action: 'delete' })
    setError('')
    setSuccess('')
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      if (!currentSession?.access_token) {
        throw new Error('No se encontro una sesion valida para eliminar usuario.')
      }

      const response = await fetch(ADMIN_DELETE_USER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: currentSession.access_token,
          user_id: userId,
        }),
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.error || 'No pudimos eliminar al usuario.')
      }

      setSuccess('Usuario eliminado correctamente.')
      await loadData()
    } catch (err) {
      console.error('[Admin] deleteUser', err)
      setError(`No pudimos eliminar al usuario.${err?.message ? ` ${err.message}` : ''}`)
    } finally {
      setSavingState({ userId: null, action: null })
    }
  }

  if (!isAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '16px', padding: '2rem', maxWidth: '420px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '500', color: C.text, marginBottom: '8px' }}>Acceso restringido</div>
          <div style={{ fontSize: '13px', color: C.muted, lineHeight: '1.6', marginBottom: '18px' }}>
            Esta vista solo esta disponible para administradores autorizados.
          </div>
          <button onClick={() => navigate('/dashboard')} style={{ padding: '10px 16px', background: C.blue, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Volver al dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <nav style={{ background: C.white, borderBottom: `0.5px solid ${C.border}`, padding: '12px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ fontSize: '15px', fontWeight: '500' }}>Job<span style={{ color: C.blue }}>Pipeline</span> Admin</div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={() => navigate('/faqs')} style={{ fontSize: '12px', color: C.muted, background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '8px 14px', cursor: 'pointer' }}>
            FAQs
          </button>
          <div onClick={() => navigate('/perfil')}
            style={{ width: '30px', height: '30px', borderRadius: '50%', background: C.blueLight, color: C.blue, fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            {session?.user?.email?.[0]?.toUpperCase() || 'A'}
          </div>
          <span onClick={() => navigate('/perfil')} style={{ fontSize: '12px', color: C.subtle, cursor: 'pointer' }}>{session?.user?.email}</span>
          <button onClick={() => navigate('/configuracion')} style={{ fontSize: '14px', color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}>⚙</button>
        </div>
      </nav>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '28px 32px', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '28px', fontWeight: '500', letterSpacing: '-0.6px', marginBottom: '4px' }}>Panel admin beta</div>
          <div style={{ fontSize: '13px', color: C.subtle }}>Aprueba o revoca acceso beta y revisa el estado del onboarding.</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
          <button
            onClick={approveAllPending}
            disabled={bulkApproving || pendingCount === 0}
            style={{ fontSize: '12px', color: '#fff', background: (bulkApproving || pendingCount === 0) ? '#94a3b8' : C.green, border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: (bulkApproving || pendingCount === 0) ? 'not-allowed' : 'pointer' }}
          >
            {bulkApproving ? 'Aprobando pendientes...' : `Aprobar pendientes (${pendingCount})`}
          </button>
          <button onClick={loadData} style={{ fontSize: '12px', color: C.blue, background: C.blueLight, border: '0.5px solid #BFDBFE', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer' }}>
            Actualizar
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
          {[
            { label: 'Pendientes', value: pendingCount, color: C.amber, bg: C.amberBg },
            { label: 'Activos beta', value: activeCount, color: C.green, bg: C.greenBg },
            { label: 'Total waitlist', value: users.length, color: C.blue, bg: C.blueLight },
          ].map((card) => (
            <div key={card.label} style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '18px 20px' }}>
              <div style={{ fontSize: '28px', fontWeight: '500', color: card.color, lineHeight: '1' }}>{card.value}</div>
              <div style={{ fontSize: '12px', color: C.subtle, marginTop: '6px' }}>{card.label}</div>
            </div>
          ))}
        </div>

        {error && <div style={{ background: C.redBg, border: `0.5px solid ${C.redBorder}`, color: C.red, padding: '12px 14px', borderRadius: '10px', fontSize: '13px', marginBottom: '14px' }}>{error}</div>}
        {success && <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, color: C.green, padding: '12px 14px', borderRadius: '10px', fontSize: '13px', marginBottom: '14px' }}>{success}</div>}

        <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1.2fr 1.4fr', gap: '12px', padding: '14px 18px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', color: C.subtle, borderBottom: `0.5px solid ${C.border}` }}>
            <div>Usuario</div>
            <div>Email</div>
            <div>Keywords</div>
            <div>Tipo</div>
            <div>Estado</div>
            <div>Onboarding</div>
            <div>Acciones</div>
          </div>

          {loading ? (
            <div style={{ padding: '24px 18px', fontSize: '13px', color: C.muted }}>Cargando usuarios...</div>
          ) : users.length === 0 ? (
            <div style={{ padding: '24px 18px', fontSize: '13px', color: C.muted }}>Aun no hay usuarios en waitlist.</div>
          ) : (
            users.map((user) => (
              <div key={user.id} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1.2fr 1.4fr', gap: '12px', padding: '16px 18px', borderTop: `0.5px solid ${C.border}`, alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '500' }}>{user.nombre}</div>
                  <div style={{ fontSize: '11px', color: C.subtle, marginTop: '4px' }}>{user.id}</div>
                </div>
                <div style={{ fontSize: '13px' }}>{user.email}</div>
                <div style={{ fontSize: '13px' }}>
                  <div style={{ fontWeight: '500' }}>{user.keywords_count}</div>
                  {Array.isArray(user.keywords_list) && user.keywords_list.length > 0 && (
                    <div style={{ fontSize: '11px', color: C.subtle, marginTop: '4px', lineHeight: '1.4' }}>
                      {user.keywords_list.slice(0, 3).join(', ')}
                      {user.keywords_list.length > 3 ? ` +${user.keywords_list.length - 3}` : ''}
                    </div>
                  )}
                </div>
                <div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', background: getUserType(user).bg, color: getUserType(user).color }}>
                    {getUserType(user).label}
                  </span>
                </div>
                <div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', background: user.beta_access ? C.greenBg : C.amberBg, color: user.beta_access ? C.green : C.amber }}>
                    {user.beta_access ? 'Beta activa' : 'Pendiente'}
                  </span>
                  {user.beta_access_mismatch && !FALLBACK_MODE && (
                    <div style={{ marginTop: '4px', fontSize: '11px', color: C.red }}>
                      Desalineado (profile vs flags)
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: C.muted }}>
                  <div>{user.onboarding_updated_at ? 'Completo' : 'Sin completar'}</div>
                  <div style={{ marginTop: '4px', color: C.subtle }}>{formatDate(user.onboarding_updated_at || user.joined_at)}</div>
                  <div style={{ marginTop: '6px', fontSize: '11px', color: user.jobs_count > 0 ? C.green : C.amber }}>
                    {user.jobs_count > 0 ? `Procesado (${user.jobs_count})` : 'Sin corrida'}
                  </div>
                  {user.last_job_at && (
                    <div style={{ marginTop: '3px', fontSize: '10px', color: C.subtle }}>
                      Ultima vacante: {formatDate(user.last_job_at)}
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {user.beta_access && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: C.text }}>
                        <input
                          type='checkbox'
                          checked={featureDrafts[user.user_id || user.id]?.premium_enabled === true}
                          onChange={(e) => toggleFeatureDraft(user.user_id || user.id, 'premium_enabled', e.target.checked)}
                        />
                        Premium habilitado
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: C.text }}>
                        <input
                          type='checkbox'
                          checked={featureDrafts[user.user_id || user.id]?.unlimited_keywords === true}
                          onChange={(e) => toggleFeatureDraft(user.user_id || user.id, 'unlimited_keywords', e.target.checked)}
                          disabled={featureDrafts[user.user_id || user.id]?.premium_enabled !== true}
                        />
                        Keywords ilimitadas
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: C.text }}>
                        <input
                          type='checkbox'
                          checked={featureDrafts[user.user_id || user.id]?.manual_daily_search === true}
                          onChange={(e) => toggleFeatureDraft(user.user_id || user.id, 'manual_daily_search', e.target.checked)}
                          disabled={featureDrafts[user.user_id || user.id]?.premium_enabled !== true}
                        />
                        Busqueda manual diaria
                      </label>
                      <button
                        onClick={() => saveUserFeatures(user.user_id || user.id)}
                        disabled={savingState.userId === (user.user_id || user.id)}
                        style={{ padding: '8px 12px', background: C.blue, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', marginBottom: '8px' }}
                      >
                        {savingState.userId === (user.user_id || user.id) && savingState.action === 'features' ? 'Guardando...' : 'Guardar features'}
                      </button>
                    </>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {!user.beta_access ? (
                      <button
                        onClick={() => setBetaAccess(user.user_id || user.id, true)}
                        disabled={savingState.userId === (user.user_id || user.id)}
                        style={{ padding: '8px 12px', background: C.blue, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}
                      >
                        {savingState.userId === (user.user_id || user.id) && savingState.action === 'approve' ? 'Aprobando...' : 'Aprobar beta'}
                      </button>
                    ) : (
                      <button
                        onClick={() => setBetaAccess(user.user_id || user.id, false)}
                        disabled={savingState.userId === (user.user_id || user.id)}
                        style={{ padding: '8px 12px', background: C.white, color: C.red, border: `0.5px solid ${C.redBorder}`, borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}
                      >
                        {savingState.userId === (user.user_id || user.id) && savingState.action === 'revoke' ? 'Actualizando...' : 'Revocar beta'}
                      </button>
                    )}
                    <button
                      onClick={() => deleteUser(user.user_id || user.id, user.email)}
                      disabled={savingState.userId === (user.user_id || user.id)}
                      style={{ padding: '8px 12px', background: '#fff7ed', color: '#c2410c', border: '0.5px solid #fed7aa', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}
                    >
                      {savingState.userId === (user.user_id || user.id) && savingState.action === 'delete' ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px', fontSize: '12px' }}>
          <button onClick={() => navigate('/privacidad')} style={{ color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}>Privacidad</button>
          <button onClick={() => navigate('/terminos')} style={{ color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}>Terminos</button>
        </div>
      </div>
    </div>
  )
}
